import { useEffect } from "react";
import {
  nativeRuntime,
  type NativeRuntime,
  type PlaybackCommand,
  type PlaybackSnapshot,
} from "../platform/native";
import { usePlayer, type PlaybackSource } from "../state/player";
import { useZone } from "../time/useZone";
import { currentFilter, playbackRequest } from "./useAudioSession";

type PlaybackRuntime = Pick<NativeRuntime, "snapshot" | "subscribe">;

function identity(snapshot: PlaybackSnapshot): string {
  return `${snapshot.sessionId}:${snapshot.accountEpoch}`;
}

export function newerRevision(previous: PlaybackSnapshot, next: PlaybackSnapshot): boolean {
  return (
    previous.sessionId === next.sessionId &&
    previous.accountEpoch === next.accountEpoch &&
    next.revision > previous.revision
  );
}

export function observeNativeSession(
  runtime: PlaybackRuntime,
  apply: (snapshot: PlaybackSnapshot) => void,
  report: (error: unknown) => void,
): () => void {
  let accepted: PlaybackSnapshot | null = null;
  let pendingReplacement: PlaybackSnapshot | null = null;
  let refreshing: Promise<void> | null = null;
  let unsubscribe: (() => void) | null = null;
  let disposed = false;
  const stale = new Set<string>();

  const accept = (next: PlaybackSnapshot, replacement: boolean): boolean => {
    if (disposed || stale.has(identity(next))) return false;
    if (accepted === null) {
      accepted = next;
      apply(next);
      return true;
    }
    if (identity(accepted) === identity(next)) {
      if (!newerRevision(accepted, next)) return false;
      accepted = next;
      apply(next);
      return true;
    }
    if (!replacement) return false;
    stale.add(identity(accepted));
    accepted = next;
    apply(next);
    return true;
  };

  const refresh = (): Promise<void> => {
    if (refreshing !== null) return refreshing;
    refreshing = (async () => {
      do {
        const hinted = pendingReplacement;
        pendingReplacement = null;
        const current = await runtime.snapshot();
        if (disposed) return;
        const next =
          hinted !== null &&
          identity(hinted) === identity(current) &&
          newerRevision(current, hinted)
            ? hinted
            : current;
        accept(next, true);
      } while (pendingReplacement !== null && !disposed);
    })()
      .catch((error: unknown) => {
        if (!disposed) report(error);
      })
      .finally(() => {
        refreshing = null;
        if (pendingReplacement !== null && !disposed) void refresh();
      });
    return refreshing;
  };

  const onSnapshot = (next: PlaybackSnapshot) => {
    if (disposed || stale.has(identity(next))) return;
    if (accepted === null || identity(accepted) === identity(next)) {
      accept(next, false);
      return;
    }
    if (
      pendingReplacement === null ||
      identity(pendingReplacement) !== identity(next) ||
      newerRevision(pendingReplacement, next)
    ) {
      pendingReplacement = next;
    }
    void refresh();
  };

  void runtime
    .subscribe(onSnapshot)
    .then((stop) => {
      if (disposed) {
        stop();
        return;
      }
      unsubscribe = stop;
      return refresh();
    })
    .catch((error: unknown) => {
      if (!disposed) report(error);
    });

  return () => {
    disposed = true;
    unsubscribe?.();
    unsubscribe = null;
  };
}

type CommandRuntime = Pick<NativeRuntime, "command">;

function observedSource(
  snapshot: PlaybackSnapshot,
  previous: PlaybackSource | null,
): PlaybackSource | null {
  if (snapshot.source === null && snapshot.presentationId === null) return null;
  if (snapshot.source?.kind === "live") {
    if (snapshot.source.at) {
      const sourceAtMs = Date.parse(snapshot.source.at);
      if (Number.isFinite(sourceAtMs)) return { kind: "dvr", sourceAtMs };
    }
    return { kind: "live" };
  }
  if (snapshot.source?.kind === "window") {
    const startMs = Date.parse(snapshot.source.at);
    if (Number.isFinite(startMs)) return { kind: "window", startMs };
  }
  if (
    snapshot.source?.kind === "segment" &&
    previous?.kind === "segment" &&
    previous.segment.id === snapshot.source.segment_id
  ) {
    return previous;
  }
  const itemPositionUs =
    snapshot.audible?.itemPositionUs ??
    (snapshot.source?.kind === "segment" ? snapshot.source.item_position_us : undefined) ??
    (previous?.kind === "presentation" ? previous.itemPositionUs : undefined);
  if (itemPositionUs === undefined) return previous;
  return {
    kind: "presentation",
    presentationId: snapshot.presentationId,
    segmentId:
      snapshot.audible?.segmentId ??
      (snapshot.source?.kind === "segment" ? snapshot.source.segment_id : null),
    itemPositionUs,
    sourceAt: snapshot.sourceAt,
  };
}

export function applyNativeSnapshot(snapshot: PlaybackSnapshot): void {
  usePlayer.setState((previous) => {
    const source = observedSource(snapshot, previous.source);
    const sourceAtMs = snapshot.sourceAt === null ? Number.NaN : Date.parse(snapshot.sourceAt);
    return {
      nativeSnapshot: snapshot,
      source,
      wantsPlayback: snapshot.wantsPlayback,
      playing: snapshot.state === "playing",
      loading: snapshot.state === "loading" || snapshot.state === "buffering",
      ...(Number.isFinite(sourceAtMs)
        ? { positionMs: sourceAtMs, positionAt: Date.now() }
        : source === null
          ? { positionAt: null }
          : {}),
      atLive: snapshot.atLive,
      ...(snapshot.atLive ? { follow: true } : {}),
      volume: snapshot.prefs.volume,
      muted: snapshot.prefs.muted,
      selectedId: source === null ? null : (snapshot.audible?.segmentId ?? previous.selectedId),
      error: snapshot.error?.code ?? null,
    };
  });
}

export function observeNativeCommands(
  runtime: CommandRuntime,
  zone: string,
  report: (error: unknown) => void,
): () => void {
  let disposed = false;
  const dispatch = (command: PlaybackCommand) => {
    void runtime.command(command).catch((error: unknown) => {
      if (!disposed) report(error);
    });
  };
  const unsubscribe = usePlayer.subscribe((state, previous) => {
    if (state.nativeSnapshot !== previous.nativeSnapshot) return;
    if (state.sourceToken !== previous.sourceToken) {
      const source = state.source;
      if (source?.kind === "presentation") dispatch({ type: "play" });
      else if (source) {
        const request = playbackRequest(source);
        if (request) dispatch({ type: "open", source: request, filter: currentFilter(zone) });
      }
      return;
    }
    if (state.source !== previous.source && state.source === null) {
      dispatch({ type: "stop" });
      return;
    }
    if (state.wantsPlayback !== previous.wantsPlayback) {
      dispatch({ type: state.wantsPlayback ? "play" : "pause" });
    }
    if (state.seekToken !== previous.seekToken && state.seekRequest) {
      dispatch({ type: "seek", target: state.seekRequest.target });
    }
    if (state.nudgeToken !== previous.nudgeToken && state.nudgeRequest) {
      dispatch({ type: "nudge", seconds: state.nudgeRequest.seconds });
    }
    if (state.stepToken !== previous.stepToken && state.stepRequest) {
      dispatch({ type: "step", direction: state.stepRequest.direction });
    }
    if (state.volume !== previous.volume || state.muted !== previous.muted) {
      dispatch({ type: "volume", volume: state.volume, muted: state.muted });
    }
  });
  return () => {
    disposed = true;
    unsubscribe();
  };
}

export function useNativeSession(): void {
  const runtime = nativeRuntime();
  const zone = useZone().name;
  useEffect(() => {
    if (runtime === null) return;
    const report = (error: unknown) => {
      usePlayer.getState().setError(error instanceof Error ? error.message : String(error));
    };
    const stopCommands = observeNativeCommands(runtime, zone, report);
    const stopSession = observeNativeSession(runtime, applyNativeSnapshot, report);
    return () => {
      stopCommands();
      stopSession();
    };
  }, [runtime, zone]);
}
